import type { CSSProperties } from "react";
import { BRAND_ACCENT, BRAND_ACCENT_BRIGHT, BRAND_ACCENT_DEEP } from "@/lib/brand";
import { BRANDBOOK } from "@/lib/brand/brandbook";

/**
 * Палитры CMS-лендингов — без color picker.
 * База: токены приложения (teal-акцент, нейтральные light/dark) + брендбук.
 * Комбинация: акцент × фон × светлость.
 */
export const LANDING_ACCENT_PALETTES = [
  { value: "brandbook", label: "Брендбук — Idealitika" },
  { value: "teal", label: "Бирюза — приложение" },
  { value: "tealDeep", label: "Бирюза глубокая" },
  { value: "mint", label: "Мята" },
  { value: "emerald", label: "Изумруд" },
  { value: "lime", label: "Лайм" },
  { value: "sky", label: "Небо" },
  { value: "azure", label: "Лазурь" },
  { value: "cyan", label: "Циан" },
  { value: "navy", label: "Тёмно-синий" },
  { value: "slate", label: "Сланец" },
  { value: "stone", label: "Камень" },
  { value: "amber", label: "Янтарь" },
  { value: "gold", label: "Золото" },
  { value: "copper", label: "Медь" },
  { value: "coral", label: "Коралл" },
  { value: "rose", label: "Роза — брендбук" },
  { value: "crimson", label: "Бордо" },
  { value: "fuchsia", label: "Фуксия" },
  { value: "violet", label: "Фиолет" },
  { value: "grape", label: "Виноград" },
] as const;

export const LANDING_SURFACE_PALETTES = [
  { value: "brandbook", label: "Брендбук — Idealitika" },
  { value: "app", label: "Как в приложении" },
  { value: "paper", label: "Тёплый (бумага)" },
  { value: "cream", label: "Кремовый" },
  { value: "linen", label: "Лён" },
  { value: "sand", label: "Песок" },
  { value: "pearl", label: "Жемчуг" },
  { value: "frost", label: "Холодный (иней)" },
  { value: "mist", label: "Туман" },
  { value: "sage", label: "Шалфей" },
  { value: "lavender", label: "Лаванда" },
  { value: "ocean", label: "Океан" },
  { value: "ink", label: "Контрастный" },
  { value: "charcoal", label: "Уголь" },
  { value: "graphite", label: "Графит" },
  { value: "midnight", label: "Полночь" },
  { value: "obsidian", label: "Обсидиан" },
  { value: "cocoa", label: "Какао" },
  { value: "dusk", label: "Сумерки" },
] as const;

export const LANDING_COLOR_SCHEMES = [
  { value: "auto", label: "Авто (как у посетителя)" },
  { value: "light", label: "Всегда светлая" },
  { value: "dark", label: "Всегда тёмная" },
] as const;

/** @deprecated используйте LANDING_COLOR_SCHEMES */
export const LANDING_APPEARANCES = [
  { value: "system", label: "Как в системе" },
  { value: "light", label: "Светлая" },
  { value: "dark", label: "Тёмная" },
] as const;

export type LandingAccentPalette =
  (typeof LANDING_ACCENT_PALETTES)[number]["value"];
export type LandingSurfacePalette =
  (typeof LANDING_SURFACE_PALETTES)[number]["value"];
export type LandingColorScheme =
  (typeof LANDING_COLOR_SCHEMES)[number]["value"];
/** @deprecated */
export type LandingAppearance = "system" | "light" | "dark";

export type LandingPageTheme = {
  title: string;
  accentPalette: LandingAccentPalette;
  /** Фон и текст в светлом режиме */
  surfaceLight: LandingSurfacePalette;
  /** Фон и текст в тёмном режиме */
  surfaceDark: LandingSurfacePalette;
  /** auto = светлая/тёмная по настройкам посетителя */
  colorScheme: LandingColorScheme;
};

export const DEFAULT_LANDING_ROOT_PROPS: LandingPageTheme = {
  title: "",
  accentPalette: "brandbook",
  surfaceLight: "brandbook",
  surfaceDark: "brandbook",
  colorScheme: "auto",
};

/** Готовый пресет из брендбука для кнопки в настройках темы. */
export const BRANDBOOK_LANDING_THEME: Pick<
  LandingPageTheme,
  "accentPalette" | "surfaceLight" | "surfaceDark" | "colorScheme"
> = {
  accentPalette: "brandbook",
  surfaceLight: "brandbook",
  surfaceDark: "brandbook",
  colorScheme: "auto",
};

type AccentTokens = { accent: string; deep: string; bright: string };
type SurfaceTokens = {
  body: string;
  muted: string;
  surface: string;
  border: string;
  text: string;
  textSecondary: string;
};

/** Акценты от бренда + соседние оттенки той же системы. */
const ACCENT_VALUES: Record<LandingAccentPalette, AccentTokens> = {
  brandbook: {
    accent: BRANDBOOK.red,
    deep: BRANDBOOK.rose,
    bright: BRANDBOOK.blush,
  },
  teal: {
    accent: BRAND_ACCENT,
    deep: BRAND_ACCENT_DEEP,
    bright: BRAND_ACCENT_BRIGHT,
  },
  tealDeep: {
    accent: BRAND_ACCENT_DEEP,
    deep: "#115e59",
    bright: BRAND_ACCENT,
  },
  mint: { accent: "#14b8a6", deep: "#0d9488", bright: "#5eead4" },
  emerald: { accent: "#059669", deep: "#047857", bright: "#6ee7b7" },
  lime: { accent: "#65a30d", deep: "#4d7c0f", bright: "#bef264" },
  sky: { accent: "#0ea5e9", deep: "#0369a1", bright: "#7dd3fc" },
  azure: { accent: "#2563eb", deep: "#1d4ed8", bright: "#93c5fd" },
  cyan: { accent: "#0891b2", deep: "#0e7490", bright: "#67e8f9" },
  navy: { accent: "#1e40af", deep: "#1e3a8a", bright: "#93c5fd" },
  slate: { accent: "#475569", deep: "#334155", bright: "#94a3b8" },
  stone: { accent: "#57534e", deep: "#44403c", bright: "#a8a29e" },
  amber: { accent: "#d97706", deep: "#b45309", bright: "#fcd34d" },
  gold: { accent: "#ca8a04", deep: "#a16207", bright: "#fde047" },
  copper: { accent: "#c2410c", deep: "#9a3412", bright: "#fdba74" },
  coral: { accent: "#f97316", deep: "#ea580c", bright: "#fdba74" },
  /** Совпадает с брендбуком (#E11D48 / #D6002E). */
  rose: {
    accent: BRANDBOOK.rose,
    deep: BRANDBOOK.red,
    bright: BRANDBOOK.blush,
  },
  crimson: { accent: "#dc2626", deep: "#b91c1c", bright: "#fca5a5" },
  fuchsia: { accent: "#c026d3", deep: "#a21caf", bright: "#f0abfc" },
  violet: { accent: "#7c3aed", deep: "#5b21b6", bright: "#c4b5fd" },
  grape: { accent: "#9333ea", deep: "#7e22ce", bright: "#d8b4fe" },
};

/** Светлые фоны — из токенов app (body/muted/surface/border). */
const SURFACE_LIGHT: Record<LandingSurfacePalette, SurfaceTokens> = {
  brandbook: {
    body: BRANDBOOK.white,
    muted: BRANDBOOK.mist,
    surface: BRANDBOOK.white,
    border: "#E5E5E8",
    text: BRANDBOOK.text,
    textSecondary: BRANDBOOK.textMuted,
  },
  app: {
    body: "#ffffff",
    muted: "#f5f5f5",
    surface: "#fafafa",
    border: "#ebebeb",
    text: "#0a0a0a",
    textSecondary: "#737373",
  },
  paper: {
    body: "#fffdf8",
    muted: "#f7f3ea",
    surface: "#fffef9",
    border: "#e8e0d4",
    text: "#1c1917",
    textSecondary: "#78716c",
  },
  cream: {
    body: "#fffbeb",
    muted: "#fef3c7",
    surface: "#fffef5",
    border: "#fde68a",
    text: "#292524",
    textSecondary: "#78716c",
  },
  linen: {
    body: "#faf8f5",
    muted: "#f0ebe3",
    surface: "#fffcf7",
    border: "#e7e0d5",
    text: "#1c1917",
    textSecondary: "#78716c",
  },
  sand: {
    body: "#faf6f1",
    muted: "#f0e8dc",
    surface: "#fffdf9",
    border: "#e8dcc8",
    text: "#292524",
    textSecondary: "#78716c",
  },
  pearl: {
    body: "#fafafa",
    muted: "#f0f0f0",
    surface: "#ffffff",
    border: "#e5e5e5",
    text: "#171717",
    textSecondary: "#737373",
  },
  frost: {
    body: "#f8fafc",
    muted: "#f1f5f9",
    surface: "#ffffff",
    border: "#e2e8f0",
    text: "#0f172a",
    textSecondary: "#64748b",
  },
  mist: {
    body: "#f4f7fb",
    muted: "#e8eef6",
    surface: "#fbfcfe",
    border: "#d8e2f0",
    text: "#0f172a",
    textSecondary: "#64748b",
  },
  sage: {
    body: "#f6f8f4",
    muted: "#e8efe3",
    surface: "#fbfdf9",
    border: "#d5e3cc",
    text: "#1a2e1a",
    textSecondary: "#5f6f5f",
  },
  lavender: {
    body: "#faf8ff",
    muted: "#f0ebff",
    surface: "#fdfcff",
    border: "#e4d9ff",
    text: "#2e1065",
    textSecondary: "#6b5b95",
  },
  ocean: {
    body: "#f0f9ff",
    muted: "#e0f2fe",
    surface: "#f8fcff",
    border: "#bae6fd",
    text: "#0c4a6e",
    textSecondary: "#0369a1",
  },
  ink: {
    body: "#ffffff",
    muted: "#ececec",
    surface: "#f3f3f3",
    border: "#cfcfcf",
    text: "#000000",
    textSecondary: "#525252",
  },
  charcoal: {
    body: "#f7f7f7",
    muted: "#ececec",
    surface: "#ffffff",
    border: "#d4d4d4",
    text: "#171717",
    textSecondary: "#525252",
  },
  graphite: {
    body: "#f4f4f5",
    muted: "#e4e4e7",
    surface: "#fafafa",
    border: "#d4d4d8",
    text: "#18181b",
    textSecondary: "#71717a",
  },
  midnight: {
    body: "#eef2ff",
    muted: "#e0e7ff",
    surface: "#f5f7ff",
    border: "#c7d2fe",
    text: "#1e1b4b",
    textSecondary: "#4338ca",
  },
  obsidian: {
    body: "#fafafa",
    muted: "#f0f0f0",
    surface: "#ffffff",
    border: "#d4d4d4",
    text: "#0a0a0a",
    textSecondary: "#525252",
  },
  cocoa: {
    body: "#faf6f3",
    muted: "#efe6df",
    surface: "#fffaf7",
    border: "#dcc8b8",
    text: "#3d2c24",
    textSecondary: "#78645a",
  },
  dusk: {
    body: "#faf5ff",
    muted: "#f3e8ff",
    surface: "#fdfaff",
    border: "#e9d5ff",
    text: "#3b0764",
    textSecondary: "#7e22ce",
  },
};

/** Тёмные фоны — как html[data-theme=dark] в приложении. */
const SURFACE_DARK: Record<LandingSurfacePalette, SurfaceTokens> = {
  brandbook: {
    body: BRANDBOOK.ink,
    muted: "#12151A",
    surface: "#1A1D22",
    border: "#2A2E36",
    text: BRANDBOOK.mist,
    textSecondary: BRANDBOOK.textMuted,
  },
  app: {
    body: "#0a0a0a",
    muted: "#171717",
    surface: "#262626",
    border: "#ffffff1a",
    text: "#fafafa",
    textSecondary: "#a3a3a3",
  },
  paper: {
    body: "#12100d",
    muted: "#1c1914",
    surface: "#221f1a",
    border: "#3d362c",
    text: "#fafaf9",
    textSecondary: "#a8a29e",
  },
  cream: {
    body: "#1a1612",
    muted: "#252017",
    surface: "#2a241c",
    border: "#3d3428",
    text: "#fafaf9",
    textSecondary: "#a8a29e",
  },
  linen: {
    body: "#14120f",
    muted: "#1e1b17",
    surface: "#25221d",
    border: "#3a342c",
    text: "#fafaf9",
    textSecondary: "#a8a29e",
  },
  sand: {
    body: "#161310",
    muted: "#211d18",
    surface: "#28241e",
    border: "#3d362c",
    text: "#fafaf9",
    textSecondary: "#a8a29e",
  },
  pearl: {
    body: "#111111",
    muted: "#1a1a1a",
    surface: "#222222",
    border: "#333333",
    text: "#fafafa",
    textSecondary: "#a3a3a3",
  },
  frost: {
    body: "#0b1220",
    muted: "#111827",
    surface: "#151d2e",
    border: "#2a3548",
    text: "#f8fafc",
    textSecondary: "#94a3b8",
  },
  mist: {
    body: "#0c1220",
    muted: "#131b2e",
    surface: "#182033",
    border: "#2a3a52",
    text: "#f1f5f9",
    textSecondary: "#94a3b8",
  },
  sage: {
    body: "#0f1410",
    muted: "#151c16",
    surface: "#1a231c",
    border: "#2d3d30",
    text: "#ecfdf5",
    textSecondary: "#86efac",
  },
  lavender: {
    body: "#120f1a",
    muted: "#1a1526",
    surface: "#221c30",
    border: "#3b2f55",
    text: "#faf5ff",
    textSecondary: "#c4b5fd",
  },
  ocean: {
    body: "#061018",
    muted: "#0c1a28",
    surface: "#102030",
    border: "#1e3a52",
    text: "#e0f2fe",
    textSecondary: "#7dd3fc",
  },
  ink: {
    body: "#000000",
    muted: "#141414",
    surface: "#1a1a1a",
    border: "#404040",
    text: "#ffffff",
    textSecondary: "#a3a3a3",
  },
  charcoal: {
    body: "#0f0f0f",
    muted: "#1a1a1a",
    surface: "#222222",
    border: "#3a3a3a",
    text: "#fafafa",
    textSecondary: "#a3a3a3",
  },
  graphite: {
    body: "#111113",
    muted: "#1c1c1f",
    surface: "#27272a",
    border: "#3f3f46",
    text: "#fafafa",
    textSecondary: "#a1a1aa",
  },
  midnight: {
    body: "#050816",
    muted: "#0b1024",
    surface: "#101836",
    border: "#1e2a5e",
    text: "#e0e7ff",
    textSecondary: "#a5b4fc",
  },
  obsidian: {
    body: "#000000",
    muted: "#0d0d0d",
    surface: "#141414",
    border: "#2e2e2e",
    text: "#ffffff",
    textSecondary: "#a3a3a3",
  },
  cocoa: {
    body: "#120e0c",
    muted: "#1c1612",
    surface: "#241c18",
    border: "#3d3028",
    text: "#fafaf9",
    textSecondary: "#a8a29e",
  },
  dusk: {
    body: "#0f0818",
    muted: "#181024",
    surface: "#201430",
    border: "#3b2660",
    text: "#f5f3ff",
    textSecondary: "#c4b5fd",
  },
};

/** Старые значения surface → новые (миграция сохранённых страниц). */
const SURFACE_ALIASES: Record<string, LandingSurfacePalette> = {
  neutral: "app",
  contrast: "ink",
};

const ACCENT_ALIASES: Record<string, LandingAccentPalette> = {
  indigo: "violet",
};

function isAccentPalette(v: string): v is LandingAccentPalette {
  return LANDING_ACCENT_PALETTES.some((p) => p.value === v);
}

function isSurfacePalette(v: string): v is LandingSurfacePalette {
  return LANDING_SURFACE_PALETTES.some((p) => p.value === v);
}

function isColorScheme(v: string): v is LandingColorScheme {
  return LANDING_COLOR_SCHEMES.some((p) => p.value === v);
}

function normalizeColorScheme(raw: string): LandingColorScheme {
  if (raw === "system") return "auto";
  return isColorScheme(raw) ? raw : "auto";
}

export function parseRootTheme(
  props: Record<string, unknown> | undefined,
): LandingPageTheme {
  if (!props) return { ...DEFAULT_LANDING_ROOT_PROPS };

  let accentRaw =
    typeof props.accentPalette === "string" ? props.accentPalette : "brandbook";
  accentRaw = ACCENT_ALIASES[accentRaw] ?? accentRaw;

  const legacySurfaceRaw =
    typeof props.surfacePalette === "string" ? props.surfacePalette : "brandbook";
  const legacySurface = SURFACE_ALIASES[legacySurfaceRaw] ?? legacySurfaceRaw;

  let surfaceLightRaw =
    typeof props.surfaceLight === "string" ? props.surfaceLight : legacySurface;
  surfaceLightRaw = SURFACE_ALIASES[surfaceLightRaw] ?? surfaceLightRaw;

  let surfaceDarkRaw =
    typeof props.surfaceDark === "string" ? props.surfaceDark : legacySurface;
  surfaceDarkRaw = SURFACE_ALIASES[surfaceDarkRaw] ?? surfaceDarkRaw;

  const schemeRaw =
    typeof props.colorScheme === "string"
      ? props.colorScheme
      : typeof props.appearance === "string"
        ? props.appearance
        : "auto";

  return {
    title: typeof props.title === "string" ? props.title : "",
    accentPalette: isAccentPalette(accentRaw) ? accentRaw : "brandbook",
    surfaceLight: isSurfacePalette(surfaceLightRaw)
      ? surfaceLightRaw
      : "brandbook",
    surfaceDark: isSurfacePalette(surfaceDarkRaw)
      ? surfaceDarkRaw
      : "brandbook",
    colorScheme: normalizeColorScheme(schemeRaw),
  };
}

export function getSurfacePaletteForMode(
  theme: Pick<LandingPageTheme, "surfaceLight" | "surfaceDark">,
  resolvedDark: boolean,
): LandingSurfacePalette {
  return resolvedDark ? theme.surfaceDark : theme.surfaceLight;
}

export function resolveLandingColorSchemeDark(
  theme: Pick<LandingPageTheme, "colorScheme">,
  systemPrefersDark?: boolean,
): boolean {
  if (theme.colorScheme === "dark") return true;
  if (theme.colorScheme === "light") return false;
  if (typeof systemPrefersDark === "boolean") return systemPrefersDark;
  return resolveSystemPrefersDark();
}

function resolveSystemPrefersDark(): boolean {
  if (typeof window === "undefined") return false;
  const htmlTheme = document.documentElement.getAttribute("data-theme");
  if (htmlTheme === "dark") return true;
  if (htmlTheme === "light") return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

export function resolveLandingThemeLabels(theme: LandingPageTheme): {
  accent: string;
  accentShort: string;
  light: string;
  dark: string;
  mode: string;
  modeShort: string;
} {
  const accent =
    LANDING_ACCENT_PALETTES.find((p) => p.value === theme.accentPalette)
      ?.label ?? theme.accentPalette;
  const light =
    LANDING_SURFACE_PALETTES.find((p) => p.value === theme.surfaceLight)
      ?.label ?? theme.surfaceLight;
  const dark =
    LANDING_SURFACE_PALETTES.find((p) => p.value === theme.surfaceDark)
      ?.label ?? theme.surfaceDark;
  const mode =
    LANDING_COLOR_SCHEMES.find((p) => p.value === theme.colorScheme)?.label ??
    theme.colorScheme;
  const modeShort =
    theme.colorScheme === "light"
      ? "Светлая"
      : theme.colorScheme === "dark"
        ? "Тёмная"
        : "Авто";
  return {
    accent,
    accentShort: accent.split(/\s*[—–-]\s*/)[0]?.trim() || accent,
    light,
    dark,
    mode,
    modeShort,
  };
}

export function formatLandingThemeSummary(theme: LandingPageTheme): string {
  const { accent, light, dark, mode } = resolveLandingThemeLabels(theme);
  return `${accent} · светлая: ${light} · тёмная: ${dark} · ${mode}`;
}

export function landingThemeDataAttributes(
  theme: LandingPageTheme,
  resolvedDark: boolean,
): {
  "data-landing-accent": LandingAccentPalette;
  "data-landing-surface-light": LandingSurfacePalette;
  "data-landing-surface-dark": LandingSurfacePalette;
  "data-landing-color-scheme": LandingColorScheme;
  "data-landing-resolved": "light" | "dark";
} {
  return {
    "data-landing-accent": theme.accentPalette,
    "data-landing-surface-light": theme.surfaceLight,
    "data-landing-surface-dark": theme.surfaceDark,
    "data-landing-color-scheme": theme.colorScheme,
    "data-landing-resolved": resolvedDark ? "dark" : "light",
  };
}

function isLightHexColor(hex: string): boolean {
  const raw = hex.trim();
  if (!raw.startsWith("#") || raw.length < 7) return false;
  const r = Number.parseInt(raw.slice(1, 3), 16);
  const g = Number.parseInt(raw.slice(3, 5), 16);
  const b = Number.parseInt(raw.slice(5, 7), 16);
  if ([r, g, b].some((v) => Number.isNaN(v))) return false;
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.62;
}

function onAccentText(accentHex: string, surfaceText: string): string {
  return isLightHexColor(accentHex) ? surfaceText : "#ffffff";
}

export function isBrandbookLandingTheme(
  theme: Pick<
    LandingPageTheme,
    "accentPalette" | "surfaceLight" | "surfaceDark"
  >,
): boolean {
  return (
    theme.accentPalette === "brandbook" ||
    theme.surfaceLight === "brandbook" ||
    theme.surfaceDark === "brandbook"
  );
}

export function landingThemeInlineStyle(
  theme: LandingPageTheme,
  resolvedDark: boolean,
): CSSProperties {
  const accent = ACCENT_VALUES[theme.accentPalette];
  const surfaceKey = getSurfacePaletteForMode(theme, resolvedDark);
  const surface = resolvedDark
    ? SURFACE_DARK[surfaceKey]
    : SURFACE_LIGHT[surfaceKey];
  const onAccent = onAccentText(accent.accent, surface.text);
  const brandbookSurface = surfaceKey === "brandbook";
  /** Hover/active меню — всегда фирменный #D6002E; в тексте акцент — deep/rose. */
  const navActive = BRANDBOOK.red;

  return {
    ["--landing-accent" as string]: accent.accent,
    ["--landing-accent-deep" as string]: accent.deep,
    ["--landing-accent-bright" as string]: accent.bright,
    ["--color-accent-teal" as string]: accent.accent,
    ["--color-accent" as string]: accent.accent,
    ["--color-on-accent" as string]: onAccent,
    ["--color-accent-muted" as string]: resolvedDark
      ? `${accent.accent}33`
      : brandbookSurface
        ? BRANDBOOK.blush
        : `${accent.accent}1a`,
    ["--color-background-teal" as string]: resolvedDark
      ? `${accent.accent}33`
      : brandbookSurface
        ? BRANDBOOK.blush
        : `${accent.accent}1a`,
    ["--color-text-teal" as string]: resolvedDark
      ? accent.bright
      : accent.deep,
    ["--color-border-teal" as string]: accent.accent,
    ["--color-background-body" as string]: surface.body,
    ["--color-background-muted" as string]: surface.muted,
    ["--color-background-surface" as string]: surface.surface,
    ["--color-background-card" as string]: surface.surface,
    ["--color-background-popover" as string]: surface.surface,
    ["--color-surface-container" as string]: surface.surface,
    ["--color-border" as string]: surface.border,
    ["--color-border-emphasized" as string]: surface.border,
    ["--color-text-primary" as string]: surface.text,
    ["--color-text-secondary" as string]: surface.textSecondary,
    ["--color-text-accent" as string]: accent.deep,
    ["--color-neutral" as string]: resolvedDark
      ? "#FFFFFF1A"
      : "#0000000F",
    // Шапка / меню / типографика — всегда по брендбуку
    ["--landing-header-logo-width" as string]: `${BRANDBOOK.headerLogoWidthPx}px`,
    ["--landing-header-logo-width-lg" as string]: `${BRANDBOOK.headerLogoWidthLgPx}px`,
    ["--landing-submenu-radius" as string]: `${BRANDBOOK.submenuRadiusPx}px`,
    ["--landing-nav-font-size" as string]: `${BRANDBOOK.nav.fontSizePx}px`,
    ["--landing-nav-font-weight" as string]: String(BRANDBOOK.nav.weight),
    ["--landing-nav-font-weight-active" as string]: String(
      BRANDBOOK.nav.weightActive,
    ),
    ["--landing-nav-color" as string]: surface.text,
    ["--landing-nav-color-muted" as string]: surface.textSecondary,
    ["--landing-nav-color-active" as string]: navActive,
    ["--landing-font-family" as string]: BRANDBOOK.fontFamily,
    ["--landing-type-h1" as string]: `${BRANDBOOK.type.h1Px}px`,
    ["--landing-type-h2" as string]: `${BRANDBOOK.type.h2Px}px`,
    ["--landing-type-h3" as string]: `${BRANDBOOK.type.h3Px}px`,
    ["--landing-type-body" as string]: `${BRANDBOOK.type.bodyPx}px`,
    ["--landing-type-body-lg" as string]: `${BRANDBOOK.type.bodyLargePx}px`,
    ["--landing-type-button" as string]: `${BRANDBOOK.type.buttonPx}px`,
    ["--landing-type-overline" as string]: `${BRANDBOOK.type.overlinePx}px`,
    ["--landing-type-values-title" as string]: `${BRANDBOOK.type.valuesTitlePx}px`,
    ["--landing-type-values-body" as string]: `${BRANDBOOK.type.valuesBodyPx}px`,
    color: surface.text,
    backgroundColor: surface.body,
    fontFamily: BRANDBOOK.fontFamily,
  };
}

/** @deprecated */
export function resolveLandingAppearanceDark(
  appearance: LandingAppearance,
): boolean {
  if (appearance === "dark") return true;
  if (appearance === "light") return false;
  return resolveSystemPrefersDark();
}

/** Для превью в админке: явно светлый или тёмный вариант. */
export function resolveThemeDarkForPreview(
  colorScheme: LandingColorScheme,
  previewDark: boolean,
): boolean {
  if (colorScheme === "dark") return true;
  if (colorScheme === "light") return false;
  return previewDark;
}

export type LandingSemanticColors = {
  pageBg: string;
  sectionBg: string;
  cardBg: string;
  textPrimary: string;
  textSecondary: string;
  border: string;
  buttonPrimaryBg: string;
  buttonPrimaryText: string;
  buttonSecondaryBg: string;
  buttonSecondaryText: string;
  buttonSecondaryBorder: string;
  badgeBg: string;
  badgeText: string;
  popupBg: string;
  popupBorder: string;
  link: string;
};

export function getLandingSemanticColors(
  theme: LandingPageTheme,
  options?: {
    resolvedDark?: boolean;
    /** Превью конкретного варианта в админке */
    previewMode?: "light" | "dark";
  },
): LandingSemanticColors {
  let dark: boolean;
  let surfaceKey: LandingSurfacePalette;

  if (options?.previewMode === "light") {
    dark = false;
    surfaceKey = theme.surfaceLight;
  } else if (options?.previewMode === "dark") {
    dark = true;
    surfaceKey = theme.surfaceDark;
  } else {
    dark =
      options?.resolvedDark ?? resolveLandingColorSchemeDark(theme);
    surfaceKey = getSurfacePaletteForMode(theme, dark);
  }

  const accent = ACCENT_VALUES[theme.accentPalette];
  const surface = dark ? SURFACE_DARK[surfaceKey] : SURFACE_LIGHT[surfaceKey];
  const buttonPrimaryText = onAccentText(accent.accent, surface.text);
  const brandbookSurface = surfaceKey === "brandbook";

  return {
    pageBg: surface.body,
    sectionBg: surface.muted,
    cardBg: surface.surface,
    textPrimary: surface.text,
    textSecondary: surface.textSecondary,
    border: surface.border,
    buttonPrimaryBg: accent.accent,
    buttonPrimaryText,
    buttonSecondaryBg: "transparent",
    buttonSecondaryText: surface.text,
    buttonSecondaryBorder: surface.border,
    badgeBg: dark
      ? `${accent.accent}33`
      : brandbookSurface
        ? BRANDBOOK.blush
        : `${accent.accent}1a`,
    badgeText: dark ? accent.bright : accent.deep,
    popupBg: surface.surface,
    popupBorder: surface.border,
    link: accent.accent,
  };
}

/** Цвет акцента для превью-чипов в админке. */
export function getAccentPreviewColor(palette: LandingAccentPalette): string {
  return ACCENT_VALUES[palette].accent;
}

export function surfacePreviewForMode(
  palette: LandingSurfacePalette,
  dark: boolean,
): { background: string; foreground: string; muted: string } {
  const s = dark ? SURFACE_DARK[palette] : SURFACE_LIGHT[palette];
  return { background: s.body, foreground: s.text, muted: s.muted };
}
